<template lang="pug">
  Wrapper.q-px-lg.q-py-xl
    h5.q-mb-xl Reported Users
    .row
      .col-12
        UserFilter(:sort.sync='sort' :search.sync='search' @click:search='debouncedSendRequest' @click:reset='reset')
    DataTable(ref='table' :columns='columns')
      template(v-if='isLoading')
        tr(v-for="n in 10" :key="n")
          td(v-for="m in columns.length" :key="m")
            q-skeleton(type='text')
      template(v-else-if='!items.length')
        tr
          td.text-center(:colspan='columns.length')
            span(v-if='search') No reports found.
            span(v-else='') There are currently no conversation yet.
      tr(v-else v-for='report in items' :key='report.id')
        td
          span.text-action(@click="openDialog(report)") View
        td {{ report.createdAt | formatDateTime }}
        td {{ getCompanyName(report.reported) | uppercase }}
        td.text-cut(style="width: 100vw; max-width: 200px;") {{ report.description }}
        td(:class="report.status === 'open' ? 'text-negative' : ''") {{ report.status | capitalize }}

    Pagination(v-if='totalPages > 1' :page.sync='currentPage' :total='totalPages')
    q-dialog(v-model='dialogVisible' persistent @hide='closeDialog()')
      q-card(style='width: 50rem; max-width: 80vw;')
        q-card-section
          h6.text-center Report Details
          div.row.justify-end(style="margin-top: -30px;")
            q-select.text-negative(
              v-model='selectedItem.status'
              label="Status"
              emit-value
              map-options
              :options='statusOptions'
              style="width: 200px"
            )
        q-card-section
          .row
            .col-6
              div
                strong Reported:
                span.text-negative.q-ma-sm {{ getCompanyName(selectedItem.reported) | uppercase }}
                span.text-action(@click="showUser(selectedItem.reported)") {{ get(selectedItem, 'reported.id') | formatId }}
              div
                strong Account Type:
                span.q-ml-sm {{ getAccountType(selectedItem.reported) }}
            .col-6
              div
                strong Reporter:
                span.q-ma-sm {{ getCompanyName(selectedItem.reporter) | capitalize }}
                span.text-action(@click="showUser(selectedItem.reporter)") {{ get(selectedItem, 'reporter.id') | formatId }}
              div
                strong Account Type:
                span.q-ml-sm {{ getAccountType(selectedItem.reporter) }}
        q-card-section
          .row
            .col-12
              div
                strong Source:
                span.q-ml-sm(v-if="selectedItem.source") {{ selectedItem.source.path }}
                span.q-ma-sm(v-if="selectedItem.source && selectedItem.source.itemId") QUOTE ID: {{ selectedItem.source.itemId | formatId }}
          .row.bg-blue-1.block.q-pa-sm
            p.q-mb-none.text-bold Description:
            p.q-mb-none {{ selectedItem.description }}
        q-card-section
          p.text-bold Admin Notes:
          q-input(type='textarea' v-model='selectedItem.note' outlined)
        q-card-actions(align='right')
          q-btn(v-close-popup flat color='muted' label='Cancel' :disable='isSaving')
          q-btn(flat color='primary' label='Save' @click="save()" :disable='isSaving')
          q-btn(flat color='primary' label='Save and Exit' @click="save(true)" :disable='isSaving')
    RFQUserDialog(v-if="selectedUser" :open.sync="userDialogVisible" :user.sync="selectedUser")
</template>

<script>
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import API from '@/apis';
import { getErrorMessage } from '@/utils';
import Wrapper from '@/components/Wrapper.vue';
import UserFilter from '@/components/UserFilter.vue';
import DataTable from '@/components/DataTableNew.vue';
import Pagination from '@/components/Pagination.vue';
import formatDateMixin from '@/utils/mixins/formatDateMixin';

export default {
  name: 'AdminReportedUsers',

  components: {
    Wrapper,
    UserFilter,
    DataTable,
    Pagination,
    RFQUserDialog: () => import('../Order/RFQDialog/RFQUserDialog.vue'),
  },

  mixins: [formatDateMixin],

  data() {
    return {
      items: [],
      totalPages: 1,
      currentPage: 1,
      sort: 'DESC',
      search: '',
      isLoading: true,
      columns: ['Action', 'Report Time', 'Reported Company', 'Description', 'Status'],
      dialogVisible: false,
      selectedItem: {},
      statusOptions: [
        { label: '<span class="text-red">Open</span>', value: 'open' },
        { label: 'Closed', value: 'closed' },
      ],
      isSaving: false,
      userDialogVisible: false,
      selectedUser: null,
    };
  },

  watch: {
    currentPage() {
      this.debouncedSendRequest();
      window.scrollTo({ top: this.$refs.table.$el.offsetTop, behavior: 'smooth' });
    },
  },

  created() {
    this.debouncedSendRequest = _debounce(this.sendRequest, 500);
    this.sendRequest();
  },

  methods: {
    get: _get,
    getParams() {
      const params = {};

      if (this.search) {
        params.search = this.search;
      }

      if (this.sort !== 'DESC') {
        params.sort = this.sort;
      }

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      return params;
    },
    async sendRequest() {
      this.isLoading = true;
      const params = this.getParams();
      try {
        const {
          data: {
            data,
            pagination: { page, pages },
          },
        } = await this.$axios.request(API.admin.getReportedUsers(params));

        this.items = data;
        this.currentPage = page;
        this.totalPages = pages;
      } catch (err) {
        this.$q.notify({
          message: getErrorMessage(err),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handler: this.sendRequest }],
        });
      } finally {
        this.isLoading = false;
      }
    },
    debouncedSendRequest() {},
    reset() {
      if (this.search === '' && this.sort === 'DESC') return;

      this.search = '';
      this.sort = 'DESC';
      this.debouncedSendRequest();
    },
    getCompanyName(user) {
      const name = _get(user, 'businessName');
      if (name) {
        return name;
      }

      return _get(user, 'company.tradingName');
    },
    getAccountType(user) {
      if (!user) return undefined;

      return user.type === 'transporter' ? 'Transporter' : 'Importer';
    },
    openDialog(report) {
      this.dialogVisible = true;
      Object.assign(this.selectedItem, report);
      this.selectedItem = { ...report };
    },
    closeDialog() {
      this.selectedItem = {};
    },
    async save(closeDialog = false) {
      if (this.selectedItem.id) {
        try {
          this.isSaving = true;
          const update = {
            id: this.selectedItem.id,
            status: this.selectedItem.status,
            note: this.selectedItem.note || '',
          };
          const { data } = await this.$axios.request(API.admin.updateReport(update));
          this.$q.notify({ message: 'Report update successfully', type: 'positive' });
          const report = this.items.find((i) => i.id === data.id);
          if (report) {
            report.status = data.status;
            report.note = data.note;
          }
          if (closeDialog === true) {
            this.dialogVisible = false;
          }
        } catch (e) {
          this.$q.notify({ message: getErrorMessage(e), type: 'negative' });
        } finally {
          this.isSaving = false;
        }
      }
    },
    async showUser(user) {
      this.selectedUser = user;
      this.userDialogVisible = true;
    },
  },
};
</script>
